// =============================================================================
// FILE: packages/renderer/src/lib/security.ts
// PURPOSE: Client for the R2 License Protection & Preview Security layer —
//   free-tier rate limiting, license-verified licensed generation with document
//   fingerprinting, and public fingerprint verification. Shared by all generators.
// =============================================================================

const FOUNDER_TOKEN_LS = 'rcd2.founder.token';

export interface SecurityConfig { free_concepts_per_day: number; free_previews_per_day: number; account_required: boolean; preview_watermark: boolean; }
export interface WatermarkInfo { name: string; projectId: string; timestamp: string; }
export interface DeliverableProtection { mode: 'preview' | 'licensed'; watermark?: WatermarkInfo; fingerprint?: { code: string; licenseId: string }; }

function adminHeaders(adminEmail?: string): Record<string, string> {
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  try { const t = localStorage.getItem(FOUNDER_TOKEN_LS); if (t) h.Authorization = `Bearer ${t}`; } catch { /* */ }
  if (adminEmail) h['X-Admin-Email'] = adminEmail;
  return h;
}

export async function fetchSecurityConfig(): Promise<SecurityConfig> {
  const r = await fetch('/api/security/config');
  if (!r.ok) throw new Error('Unavailable');
  return r.json();
}

export async function updateSecurityConfig(patch: Partial<SecurityConfig>, adminEmail?: string): Promise<{ config: SecurityConfig }> {
  const r = await fetch('/api/admin/security/config', { method: 'PUT', headers: adminHeaders(adminEmail), body: JSON.stringify(patch) });
  if (!r.ok) throw new Error('Update failed');
  return r.json();
}

/** Enforce free-tier limits. Returns allowance + remaining. 401 if account required. */
export async function requestPreview(input: { email?: string; kind?: 'concept' | 'preview_export'; generator?: string; project_id?: string }):
  Promise<{ allowed: boolean; remaining: number; limit: number; licensed: boolean; reason?: string }> {
  const r = await fetch('/api/generate/preview', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ kind: 'preview_export', ...input }) });
  if (r.status === 401) throw new Error('Account required — please sign in to generate previews.');
  if (!r.ok) throw new Error('Preview request failed');
  return r.json();
}

/** Verify a license and obtain a fingerprint for a full licensed deliverable. */
export async function requestLicensed(input: { license_id: string; email?: string; generator?: string; project_id?: string; project_name?: string }):
  Promise<{ authorized: boolean; fingerprint: string; license_id: string; project_id: string; customer_name?: string; product_name?: string; issued_at: string }> {
  const r = await fetch('/api/generate/licensed', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input) });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'License verification failed');
  return r.json();
}

export async function verifyFingerprint(code: string): Promise<{
  valid: boolean; code: string; product_name?: string; project_id?: string; project_name?: string;
  generator?: string; issued_at?: string; license_id?: string; customer?: string; license_status?: string;
}> {
  const r = await fetch(`/api/verify/${encodeURIComponent(code.trim())}`);
  if (!r.ok) throw new Error('Verify failed');
  return r.json();
}
